/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable no-unused-vars */
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { ToastContainer } from 'react-toastify'
import i18n from 'i18next'

import api from '@/api/index'
import { useStorage } from '@/hooks/index'
import { getStorageValue, setStorageValue } from '@/utils'
import { ISignInRes, Maybe, TLang, TCurrency } from '@/types'
import {
  GeneralSettingsObjectType,
  MeNode,
  useMeQuery,
  useGeneralSettingsQuery,
} from '@/apollo/graphql'
import {
  ACCESS_TOKEN_KEY,
  REFRESH_TOKEN_KEY,
  DEFAULT_LANG,
  LANG_KEY,
} from '@/constants'
import 'react-toastify/dist/ReactToastify.css'

interface SignInParams {
  username?: string
  password?: string
  accessToken?: string
}

type Callback = (status: boolean, message?: string) => void
interface AppContext {
  authenticated: boolean
  appLoading: boolean
  settingsLoading: boolean
  isRTL: boolean
  currency: TCurrency
  language: string
  me: Maybe<Partial<MeNode>>
  generalSettings: Maybe<GeneralSettingsObjectType>
  onSignIn: (params: SignInParams, callback?: Callback) => void
  onSignOut: (callback?: () => void) => void
  setAppLoading: (v: boolean) => void
  changeLang: (lng: TLang) => void
}

export const AppContext = React.createContext<AppContext>({
  authenticated: false,
  appLoading: true,
  settingsLoading: true,
  isRTL: false,
  language: DEFAULT_LANG,
  currency: DEFAULT_LANG === 'he' ? 'ILS' : 'USD',
  me: undefined,
  generalSettings: null,
  onSignIn: () => null,
  onSignOut: () => null,
  setAppLoading: () => null,
  changeLang: () => null,
})

export const AppContextProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [, setAccessToken, unsetAccessToken] = useStorage<string | undefined>(
    ACCESS_TOKEN_KEY,
    undefined,
  )
  const [, setRefreshToken] = useStorage<string | undefined>(
    REFRESH_TOKEN_KEY,
    undefined,
  )
  const [lang, setLang] = useState<TLang>(DEFAULT_LANG)
  const [currency, setCurrency] = useState<TCurrency>(
    DEFAULT_LANG === 'he' ? 'ILS' : 'USD',
  )

  const [authenticated, setAuthenticated] = useState<boolean>(false)
  const [appLoading, setAppLoading] = useState<boolean>(true)
  const [me, setMe] = useState<Maybe<Partial<MeNode>>>()
  const [generalSettings, setGeneralSettings] =
    useState<Maybe<GeneralSettingsObjectType>>(undefined)

  const authToken = useMemo(() => getStorageValue(ACCESS_TOKEN_KEY), [])

  const { loading: meLoading } = useMeQuery({
    fetchPolicy: 'network-only',
    skip: !authToken,
    onCompleted: res => {
      setMe(res.me)
    },
  })

  const { loading: settingsLoading } = useGeneralSettingsQuery({
    skip: !authenticated,
    onCompleted: res => {
      setGeneralSettings(res.generalSettings)
    },
  })

  useEffect(() => {
    const lng = getStorageValue<TLang>(LANG_KEY, DEFAULT_LANG)
    i18n.changeLanguage(lng)
    setLang(lng)
    setCurrency(lng === 'he' ? 'ILS' : 'USD')
    document.documentElement.lang = lng
  }, [])

  useEffect(() => {
    setAppLoading(true)
    setAuthenticated(!!authToken)
    setAppLoading(false)
  }, [authToken])

  const onChangeLanguage = useCallback((lng: TLang) => {
    i18n.changeLanguage(lng)
    setLang(lng)
    setCurrency(lng === 'he' ? 'ILS' : 'USD')
    setStorageValue<TLang>(LANG_KEY, lng)
    window.location.reload()
  }, [])

  const onSignIn = useCallback(
    (data: SignInParams, callback?: Callback) => {
      api
        .post<ISignInRes>('users/api/token/', data)
        .then((res: ISignInRes) => {
          setAccessToken(`JWT ${res.access}`, true)
          setRefreshToken(res.refresh, true)
          if (res.access && res.refresh) {
            setAuthenticated(true)
          }
          if (callback) callback(true)
        })
        .catch(() => {
          if (callback) callback(false)
        })
    },
    [setAccessToken, setRefreshToken],
  )

  const onSignOut = useCallback(
    (callback?: () => void) => {
      unsetAccessToken()
      if (callback) callback()
    },
    [unsetAccessToken],
  )

  const value = useMemo(
    () => ({
      authenticated,
      appLoading: appLoading || meLoading || settingsLoading,
      settingsLoading,
      isRTL: lang === 'he',
      language: i18n.language,
      currency,
      me,
      generalSettings,
      onSignIn,
      onSignOut,
      setAppLoading,
      changeLang: onChangeLanguage,
    }),
    [
      appLoading,
      authenticated,
      currency,
      generalSettings,
      lang,
      me,
      meLoading,
      settingsLoading,
      onChangeLanguage,
      onSignIn,
      onSignOut,
    ],
  )

  return (
    <AppContext.Provider value={value}>
      {children}
      <ToastContainer />
    </AppContext.Provider>
  )
}
